import { GQL } from '@aline/graphql';
import { z } from 'zod';

import { ReportGridFilterModelSchema } from './report-filter';
import { reportPreviewColumnsSchema, reportPreviewFilterSchema } from './report-preview';

const ReportPlanBaseSchema = z.object({
  columns: reportPreviewColumnsSchema,
  fileFields: z.array(z.nativeEnum(GQL.ReportColumnFileField)).optional().default([]),
  /**
   * The data question in the request that the report's answers should settle,
   * written to stand alone. When the user chooses "Create and run", the agent
   * waits for the run and answers it. Absent when only a report was asked for.
   */
  followUpQuestion: z.string().optional(),
  goal: z.string(),
  /**
   * Optional user-facing instructions for filtering inside the report once
   * extraction has run — for asks the plan's document filters cannot express
   * (e.g. "filter Expiration Date on or before today" for expired contracts).
   * Emitted only when the request needs such a post-creation step.
   */
  howToUse: z.string().optional(),
  name: z.string(),
});

/** Human-readable proposal emitted by the report agent. */
export const ReportPlanProposalSchema = ReportPlanBaseSchema.extend({
  filters: z.array(reportPreviewFilterSchema).optional().default([]),
  logicOperator: z.enum(['and', 'or']).optional().default('and'),
});
export type ReportPlanProposal = z.infer<typeof ReportPlanProposalSchema>;
export type ReportPlanProposalInput = z.input<typeof ReportPlanProposalSchema>;

/**
 * Approval-card plan after the server has resolved entity names and rewritten
 * semantic operators into the canonical Report grid filter model.
 */
export const ResolvedReportPlanSchema = ReportPlanBaseSchema.extend({
  /**
   * Set by the server when the org has no AI tokens left, so the card can
   * disable "Create and run" and say why. Never set by the model.
   */
  aiUsageBlockedReason: z.string().optional(),
  filterModel: ReportGridFilterModelSchema,
  /** Set by the card once the user confirmed a run past `LARGE_RUN_ANSWER_COUNT`. */
  largeRunConfirmed: z.boolean().optional(),
  /**
   * Set by the card's "Create and run", never by the model: the model's plan
   * is rebuilt from named fields before the card sees it.
   */
  runAfterCreate: z.boolean().optional(),
});
export type ResolvedReportPlan = z.infer<typeof ResolvedReportPlanSchema>;
export type ResolvedReportPlanInput = z.input<typeof ResolvedReportPlanSchema>;

/** Backwards-compatible aliases for the AI proposal contract. */
export const ReportPlanSchema = ReportPlanProposalSchema;
export type ReportPlan = ReportPlanProposal;
export type ReportPlanInput = ReportPlanProposalInput;

/**
 * The chat card for a report the AI created from an approved plan. The report
 * exists when the card is emitted; the card is a link to that report.
 */
export const ReportAiGenerationResultSchema = z.object({
  columnCount: z.number().int().nonnegative(),
  name: z.string(),
  reportId: z.uuid(),
  type: z.literal('report-generation'),
});
export type ReportAiGenerationResult = z.infer<typeof ReportAiGenerationResultSchema>;
